import React from 'react';

import styled from 'styled-components';

import CdnImage from '~/shared/components/CdnImage';
import { Link } from '~/techstyle-shared/next-routes';
import { AssetContainer } from '~/techstyle-shared/react-assets';
import {
  useBreakpoint,
  Button,
  mobile,
} from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';
import { useHydrationStatus } from '~/techstyle-shared/redux-core';

// Note: this implementation includes a fair number of CSS hacks for aspect
// ratio that could be swapped out for much cleaner CSS once the aspect-ratio
// property is better supported *. The implementation using that can be found in
// the commit before this one (9ca680e9, at this point in time).
// * In November 2021, aspect-ratio only works on 77.23% of browsers, according
//   to https://caniuse.com/mdn-css_properties_aspect-ratio

const MOBILE_VIDEO_WIDTH = 1100;
const MOBILE_VIDEO_HEIGHT = 1659;
const DESKTOP_VIDEO_WIDTH = 2880;
const DESKTOP_VIDEO_HEIGHT = 1130;

const Wrapper = styled.div`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  display: flex;

  /* Provide a background to read the white text on before the video loads in */
  background: ${({ theme }) => theme.colors.fitAppPageBannerBg};

  /*
   * A CSS hack to keep the aspect ratio of the container equal
   * to that of the video when the viewport is wide.
   */
  &::before {
    float: left;
    content: '';
    padding-bottom: calc(
      100% * ${DESKTOP_VIDEO_HEIGHT} / ${DESKTOP_VIDEO_WIDTH}
    );
  }
  &::after {
    display: table;
    content: '';
    clear: both;
  }

  ${mobile`
    &::before {
      padding-bottom: calc(100% * ${MOBILE_VIDEO_HEIGHT} / ${MOBILE_VIDEO_WIDTH});
    }
  `}
`;

const VideoWrapper = styled.div`
  height: 100%;
  min-width: 100%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

/**
 * Ensures that the width of the parent VideoWrapper container, which sometimes
 * exceeds the width of the viewport, responds to height changes originating
 * from tall overlay text content.
 * Inspired by: {@link https://stackoverflow.com/a/58249028}
 */
const SizeHelper = styled.svg`
  display: block;
  width: auto;
  min-height: 100%;

  /*
   * Just in case a CSS reset or user agent stylesheet fiddles with max-width on
   * svg elements
   */
  max-width: none;
`;

const StyledAssetContainer = styled(AssetContainer)`
  position: absolute;
  top: 0;
  height: 100%;
  width: 100%;

  iframe {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
  }
`;

const Overlay = styled.div`
  color: ${({ theme }) => theme.colors.textLight};
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  padding: 0 32px;
  text-align: center;
`;

const Disclaimer = styled.div`
  padding-bottom: ${({ theme }) => theme.sizes.xxxl}px;
`;

const Slogan = styled.div`
  font-weight: bold;
  font-size: 30px;
  line-height: 33px;
  width: 81%;
  padding: 20px 0 10px 0;
`;

const TextCopy = styled.div`
  font-weight: 600;
  font-size: 20px;
  line-height: 24px;
  padding: 10px 0;
`;

const NonMember = styled.div`
  font-weight: bold;
  font-size: 20px;
  line-height: 24px;
  padding: 10px 0;
`;

const StyledButton = styled(Button)`
  margin: 26px 0 4px 0;
`;

export default function FitAppPageBanner() {
  const { isMobile } = useBreakpoint();
  const isHydrated = useHydrationStatus();

  return (
    <Wrapper>
      <VideoWrapper>
        <SizeHelper
          xmlns="http://www.w3.org/2000/svg"
          {...(isMobile
            ? { height: MOBILE_VIDEO_HEIGHT, width: MOBILE_VIDEO_WIDTH }
            : { height: DESKTOP_VIDEO_HEIGHT, width: DESKTOP_VIDEO_WIDTH })}
        />
        {
          // The isHydrated condition prevents an initial load of the mobile
          // video iframe when loading on desktop, which creates a jarring and
          // buggy appearance. If the CMS asset is reworked so both mobile and
          // desktop use the same container name, this will no longer be
          // necessary.
          isHydrated && (
            <StyledAssetContainer
              name={
                isMobile
                  ? 'fit_page_herobanner_mobile'
                  : 'fit_page_herobanner_desktop'
              }
            />
          )
        }
      </VideoWrapper>
      <Overlay>
        <div style={{ flex: '3 1 16px' }} />
        <CdnImage
          path="fitness-app-white-icon.png"
          localize="fitness_app"
          alt="Fit"
        />
        <Slogan>
          <FormattedMessage
            defaultMessage="FITNESS AT YOUR FINGERTIPS"
            id="fitness_app.slogan"
          />
        </Slogan>
        <TextCopy>
          <FormattedMessage
            defaultMessage="Exercise anytime, anywhere with our workout app - one of our many VIP member perks!"
            id="fitness_app.copy"
          />
        </TextCopy>
        <NonMember>
          <FormattedMessage
            defaultMessage="Non-members pay $14.99/month"
            id="fitness_app.nonmember"
          />
        </NonMember>
        <Link
          href="https://apps.apple.com/us/app/fabletics-fit/id1505326423"
          passHref
        >
          <StyledButton>
            <FormattedMessage
              defaultMessage="DOWNLOAD NOW"
              id="fitness_app.download"
            />
          </StyledButton>
        </Link>
        <div style={{ flex: '1 1 auto' }} />
        <Disclaimer>
          <FormattedMessage
            defaultMessage="Free once enrolled in VIP membership program (see site for terms & conditions)"
            id="fitness_app.disclaimer"
          />
        </Disclaimer>
      </Overlay>
    </Wrapper>
  );
}
